import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { masterSupabase as supabase } from '../lib/supabase-master';
import * as XLSX from 'xlsx';
import { Upload, CheckCircle, AlertCircle, FileText, Database, RefreshCw, ArrowRight, Building, Users, Home, CreditCard } from 'lucide-react';
import { ExcelFieldMapper, PDFContractMapper, DATEVFieldMapper } from '../services/SmartFieldMapperIntegration';
import confetti from 'canvas-confetti';

// Add animated gradient styles and scrollbar hiding
const animatedGradientStyle = `
  @keyframes gradientShift {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  .animated-gradient {
    background-size: 200% 200%;
    animation: gradientShift 15s ease infinite;
  }
  /* Hide scrollbars */
  .hide-scrollbar {
    scrollbar-width: none; /* Firefox */
    -ms-overflow-style: none; /* IE and Edge */
  }
  .hide-scrollbar::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleEl = document.createElement('style');
  styleEl.textContent = animatedGradientStyle;
  if (!document.head.querySelector('style[data-gradient-animation]')) {
    styleEl.setAttribute('data-gradient-animation', 'true');
    document.head.appendChild(styleEl);
  }
}

// Real Data Extraction Services
class RealDataExtractor {
  constructor() {
    this.ocrEndpoint = '/api/ocr-service';
    this.datevEndpoint = '/api/datev-integration';
  }

  // Extract data from PDF contracts
  async extractContractData(file) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('type', 'contract');

    try {
      const response = await fetch(this.ocrEndpoint, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('OCR failed');
      
      const result = await response.json();
      
      return {
        landlord: result.landlord || '',
        landlordEmail: result.landlord_email || '',
        landlordPhone: result.landlord_phone || '',
        tenantName: result.tenant_name || '',
        tenantEmail: result.tenant_email || '',
        tenantPhone: result.tenant_phone || '',
        propertyAddress: result.property_address || '',
        unitIdentifier: result.unit_identifier || result.apartment || '',
        monthlyRent: parseFloat(result.monthly_rent) || 0,
        coldRent: parseFloat(result.cold_rent) || 0,
        utilitiesCost: parseFloat(result.utilities_cost) || 0,
        deposit: parseFloat(result.deposit) || 0,
        leaseStart: result.lease_start || null,
        leaseEnd: result.lease_end || null,
        sizeSqm: parseFloat(result.size_sqm) || 0,
        rooms: parseFloat(result.rooms) || 0,
        confidence: result.confidence || 0.7
      };
    } catch (error) {
      console.error('Contract extraction failed:', error);
      throw new Error('Vertragsdaten konnten nicht extrahiert werden');
    }
  }

  // Extract data from Excel/CSV files
  async extractExcelData(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Intelligent column mapping
          const mappedData = this.mapExcelColumns(jsonData);
          resolve(mappedData);
        } catch (error) {
          reject(new Error('Excel-Datei konnte nicht verarbeitet werden'));
        }
      };
      reader.readAsBinaryString(file);
    });
  }

  // Intelligent column mapping for Excel data
  mapExcelColumns(data) {
    if (!data || data.length === 0) return [];

    const firstRow = data[0];
    const columnMap = this.detectColumns(Object.keys(firstRow));

    return data.map(row => ({
      // Owner data
      ownerName: row[columnMap.ownerName] || '',
      ownerEmail: row[columnMap.ownerEmail] || '',
      ownerPhone: row[columnMap.ownerPhone] || '',
      
      // Property data
      propertyName: row[columnMap.propertyName] || row[columnMap.address] || '',
      address: row[columnMap.address] || '',
      city: row[columnMap.city] || '',
      postalCode: row[columnMap.postalCode] || '',
      propertyType: row[columnMap.propertyType] || 'Mehrfamilienhaus',
      
      // Unit data
      unitIdentifier: row[columnMap.unitIdentifier] || '',
      sizeSqm: parseFloat(row[columnMap.sizeSqm]) || 0,
      rooms: parseFloat(row[columnMap.rooms]) || 0,
      monthlyRent: parseFloat(row[columnMap.monthlyRent]) || 0,
      
      // Tenant data
      tenantName: row[columnMap.tenantName] || '',
      tenantEmail: row[columnMap.tenantEmail] || '',
      tenantPhone: row[columnMap.tenantPhone] || '',
      leaseStart: row[columnMap.leaseStart] || null,
      leaseEnd: row[columnMap.leaseEnd] || null,
      
      confidence: 0.8
    }));
  }

  // Detect column types based on headers
  detectColumns(headers) {
    const map = {};
    
    headers.forEach(header => {
      const lower = header.toLowerCase();
      
      // Owner columns
      if (lower.includes('eigentümer') || lower.includes('vermieter') || lower.includes('owner')) {
        map.ownerName = header;
      } else if (lower.includes('eigentümer') && lower.includes('email')) {
        map.ownerEmail = header;
      }
      
      // Property columns
      else if (lower.includes('adresse') || lower.includes('address') || lower.includes('straße')) {
        map.address = header;
      } else if (lower.includes('stadt') || lower.includes('city')) {
        map.city = header;
      } else if (lower.includes('plz') || lower.includes('postal')) {
        map.postalCode = header;
      } else if (lower.includes('objekt') || lower.includes('property')) {
        map.propertyName = header;
      }
      
      // Unit columns
      else if (lower.includes('wohnung') || lower.includes('unit') || lower.includes('apartment')) {
        map.unitIdentifier = header;
      } else if (lower.includes('qm') || lower.includes('größe') || lower.includes('size')) {
        map.sizeSqm = header;
      } else if (lower.includes('zimmer') || lower.includes('rooms')) {
        map.rooms = header;
      } else if (lower.includes('miete') || lower.includes('rent')) {
        map.monthlyRent = header;
      }
      
      // Tenant columns
      else if (lower.includes('mieter') || lower.includes('tenant') || lower.includes('name')) {
        map.tenantName = header;
      } else if (lower.includes('mieter') && lower.includes('email')) {
        map.tenantEmail = header;
      } else if (lower.includes('mietbeginn') || lower.includes('start')) {
        map.leaseStart = header;
      } else if (lower.includes('mietende') || lower.includes('end')) {
        map.leaseEnd = header;
      }
    });
    
    return map;
  }

  // Process DATEV export
  async processDatevExport(file) {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(this.datevEndpoint, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('DATEV processing failed');
      
      return await response.json();
    } catch (error) {
      console.error('DATEV processing failed:', error);
      throw new Error('DATEV-Daten konnten nicht verarbeitet werden');
    }
  }
}

// Real Database Writer
class RealDatabaseWriter {
  constructor(userId) {
    this.userId = userId;
  }

  // Create complete property structure from contract data
  async createFromContract(contractData) {
    const results = {
      owners: [],
      properties: [],
      units: [],
      tenants: [],
      errors: []
    };

    try {
      // 1. Create or find owner
      const ownerId = await this.createOwner({
        name: contractData.landlord,
        email: contractData.landlordEmail,
        phone: contractData.landlordPhone,
        owner_type: 'individual',
        created_by: this.userId
      });
      results.owners.push(ownerId);

      // 2. Create property
      const propertyId = await this.createProperty({
        property_name: contractData.propertyAddress,
        address: contractData.propertyAddress,
        owner_id: ownerId,
        property_type: 'Mehrfamilienhaus',
        property_status: 'active',
        created_by: this.userId
      });
      results.properties.push(propertyId);

      // 3. Create unit
      const unitId = await this.createUnit({
        property_id: propertyId,
        unit_identifier: contractData.unitIdentifier,
        size_sqm: contractData.sizeSqm,
        rooms: contractData.rooms,
        monthly_rent: contractData.monthlyRent,
        status: 'occupied',
        created_by: this.userId
      });
      results.units.push(unitId);

      // 4. Create tenant
      const tenantId = await this.createTenant({
        name: contractData.tenantName,
        email: contractData.tenantEmail,
        phone: contractData.tenantPhone,
        property_id: propertyId,
        unit_id: unitId,
        unit_identifier: contractData.unitIdentifier,
        monthly_rent: contractData.monthlyRent,
        cold_rent: contractData.coldRent,
        utilities_cost: contractData.utilitiesCost,
        deposit: contractData.deposit,
        lease_start: contractData.leaseStart,
        lease_end: contractData.leaseEnd,
        status: 'active',
        tenant_status: 'active',
        created_by: this.userId
      });
      results.tenants.push(tenantId);

      return results;
    } catch (error) {
      results.errors.push(error.message);
      return results;
    }
  }

  // Bulk create from Excel data
  async createFromExcelData(excelData) {
    const results = {
      owners: [],
      properties: [],
      units: [],
      tenants: [],
      errors: []
    };

    for (const row of excelData) {
      try {
        // Group by property to avoid duplicates
        const propertyKey = `${row.address}_${row.city}`;
        let propertyId = this.propertyCache?.[propertyKey];

        if (!propertyId) {
          // Create owner
          const ownerId = await this.createOwner({
            name: row.ownerName || 'Unbekannt',
            email: row.ownerEmail,
            phone: row.ownerPhone,
            owner_type: 'individual',
            created_by: this.userId
          });
          results.owners.push(ownerId);

          // Create property
          propertyId = await this.createProperty({
            property_name: row.propertyName,
            address: row.address,
            city: row.city,
            postal_code: row.postalCode,
            owner_id: ownerId,
            property_type: row.propertyType,
            property_status: 'active',
            created_by: this.userId
          });
          results.properties.push(propertyId);

          // Cache for next rows
          if (!this.propertyCache) this.propertyCache = {};
          this.propertyCache[propertyKey] = propertyId;
        }

        // Create unit
        const unitId = await this.createUnit({
          property_id: propertyId,
          unit_identifier: row.unitIdentifier,
          size_sqm: row.sizeSqm,
          rooms: row.rooms,
          monthly_rent: row.monthlyRent,
          status: row.tenantName ? 'occupied' : 'vacant',
          created_by: this.userId
        });
        results.units.push(unitId);

        // Create tenant if exists
        if (row.tenantName) {
          const tenantId = await this.createTenant({
            name: row.tenantName,
            email: row.tenantEmail,
            phone: row.tenantPhone,
            property_id: propertyId,
            unit_id: unitId,
            unit_identifier: row.unitIdentifier,
            monthly_rent: row.monthlyRent,
            lease_start: row.leaseStart,
            lease_end: row.leaseEnd,
            status: 'active',
            tenant_status: 'active',
            created_by: this.userId
          });
          results.tenants.push(tenantId);
        }

      } catch (error) {
        results.errors.push(`Zeile ${excelData.indexOf(row) + 1}: ${error.message}`);
      }
    }

    return results;
  }

  // Individual create methods
  async createOwner(ownerData) {
    // Check if owner already exists
    const { data: existing } = await supabase
      .from('owners')
      .select('id')
      .eq('email', ownerData.email)
      .eq('created_by', this.userId)
      .single();

    if (existing) return existing.id;

    const { data, error } = await supabase
      .from('owners')
      .insert(ownerData)
      .select('id')
      .single();

    if (error) throw new Error(`Owner creation failed: ${error.message}`);
    return data.id;
  }

  async createProperty(propertyData) {
    const { data, error } = await supabase
      .from('properties')
      .insert(propertyData)
      .select('id')
      .single();

    if (error) throw new Error(`Property creation failed: ${error.message}`);
    return data.id;
  }

  async createUnit(unitData) {
    const { data, error } = await supabase
      .from('units')
      .insert(unitData)
      .select('id')
      .single();

    if (error) throw new Error(`Unit creation failed: ${error.message}`);
    return data.id;
  }

  async createTenant(tenantData) {
    const { data, error } = await supabase
      .from('tenants')
      .insert(tenantData)
      .select('id')
      .single();

    if (error) throw new Error(`Tenant creation failed: ${error.message}`);
    return data.id;
  }
}

// Main Real Onboarding Component
const RealOnboardingSystem = ({ onComplete }) => {
  const navigate = useNavigate();
  // ✅ FIX: Use useAuth hook internally instead of relying on props
  const { user, isLoading: authLoading } = useAuth();
  const [stage, setStage] = useState('welcome');
  const [selectedPath, setSelectedPath] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [extractedData, setExtractedData] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  // ✅ Smart Field Mapper integration
  const [showFieldMapper, setShowFieldMapper] = useState(false);
  const [mappedData, setMappedData] = useState(null);

  const extractor = new RealDataExtractor();
  // ✅ FIX: Initialize dbWriter with user.id from useAuth hook
  const dbWriter = React.useMemo(
    () => new RealDatabaseWriter(user?.id || "anonymous"),
    [user?.id]
  );

  const handlePathSelection = (path) => {
    setSelectedPath(path);
    setStage('upload');
  };

  const handleFileUpload = async (files) => {
    setUploadedFiles(files);
    setIsProcessing(true);
    setProcessingProgress(0);
    setError(null);

    try {
      const extractedData = [];
      
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProcessingProgress((i / files.length) * 50);

        if (selectedPath === 'PDF' && file.type === 'application/pdf') {
          const contractData = await extractor.extractContractData(file);
          extractedData.push({ type: 'contract', data: contractData, file: file.name });
        } 
        else if (selectedPath === 'EXCEL' && (file.name.endsWith('.xlsx') || file.name.endsWith('.csv'))) {
          const excelData = await extractor.extractExcelData(file);
          extractedData.push({ type: 'excel', data: excelData, file: file.name });
        }
        else if (selectedPath === 'SOFTWARE' && file.name.endsWith('.zip')) {
          const datevData = await extractor.processDatevExport(file);
          extractedData.push({ type: 'datev', data: datevData, file: file.name });
        }
      }

      setExtractedData(extractedData);
      setProcessingProgress(50);
      
      // ✅ Show Smart Field Mapper for Excel/DATEV data
      if (selectedPath === 'EXCEL' || selectedPath === 'SOFTWARE') {
        setShowFieldMapper(true);
        setStage('mapping');
      } else {
        setStage('preview');
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDataConfirmation = async () => {
    setIsProcessing(true);
    setProcessingProgress(50);

    try {
      const allResults = {
        owners: [],
        properties: [],
        units: [],
        tenants: [],
        errors: []
      };

      for (const item of extractedData) {
        setProcessingProgress(50 + (extractedData.indexOf(item) / extractedData.length) * 40);

        if (item.type === 'contract') {
          const result = await dbWriter.createFromContract(item.data);
          this.mergeResults(allResults, result);
        } 
        else if (item.type === 'excel') {
          const result = await dbWriter.createFromExcelData(item.data);
          this.mergeResults(allResults, result);
        }
        else if (item.type === 'datev') {
          // DATEV processing would go here
          console.log('DATEV processing not yet implemented');
        }
      }

      setResults(allResults);
      setProcessingProgress(100);
      setStage('success');

      // Mark onboarding as completed
      await supabase
        .from('user_preferences')
        .upsert({
          userId: user?.id || "anonymous",
          onboardingCompleted: true,
          detectedPath: selectedPath,
          completedAt: new Date().toISOString()
        });

    } catch (error) {
      setError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const mergeResults = (target, source) => {
    target.owners.push(...source.owners);
    target.properties.push(...source.properties);
    target.units.push(...source.units);
    target.tenants.push(...source.tenants);
    target.errors.push(...source.errors);
  };

  const renderWelcome = () => (
    <div className="min-h-screen bg-gradient-to-br from-cyan-400 via-purple-500 to-pink-500 animated-gradient">
      {/* Hero Section */}
      <div className="relative overflow-hidden py-20 px-6">
        <div className="absolute inset-0 bg-grid-white/10"></div>
        <div className="relative max-w-6xl mx-auto text-center text-white">
          <div className="inline-block mb-6">
            <h1 className="text-7xl md:text-8xl font-black tracking-tight drop-shadow-2xl animate-pulse">
              Clara360
            </h1>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Willkommen in Ihrer neuen Immobilienverwaltung!
          </h2>
          <p className="text-xl md:text-2xl mb-8 text-blue-100">
            In nur 3 Schritten sind Sie startklar
          </p>
          
          {/* Progress Stepper */}
          <div className="flex flex-wrap justify-center items-center gap-4 mb-8">
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
              <div className="w-8 h-8 rounded-full bg-white text-blue-600 flex items-center justify-center font-bold">1</div>
              <span className="text-sm font-medium">Daten importieren</span>
            </div>
            <ArrowRight className="h-5 w-5" />
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full opacity-60">
              <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center">2</div>
              <span className="text-sm">Prüfen</span>
            </div>
            <ArrowRight className="h-5 w-5 opacity-60" />
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full opacity-60">
              <div className="w-8 h-8 rounded-full bg-white/30 flex items-center justify-center">3</div>
              <span className="text-sm">Fertig</span>
            </div>
          </div>
        </div>
      </div>

      {/* Path Selection Cards */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-8">
        {/* PDF Card */}
        <div 
          style={{maxHeight: '600px', overflowY: 'hidden'}} className="group relative bg-white/95 backdrop-blur-md p-8 rounded-2xl shadow-2xl cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.4)] flex flex-col"
          onClick={() => handlePathSelection('PDF')}
        >
          {/* Gradient Glow on Hover */}
          <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-orange-400 opacity-0 group-hover:opacity-20 rounded-2xl transition-opacity duration-300"></div>
          
          {/* Icon with Glow */}
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-red-400 blur-2xl opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
            <FileText className="relative h-16 w-16 text-red-500 mx-auto group-hover:scale-110 group-hover:animate-pulse transition-transform duration-300" />
          </div>
          
          <h3 className="text-2xl font-bold mb-3 text-gray-900 text-center">PDF-Dokumente</h3>
          <p className="text-base text-gray-600 mb-6 leading-relaxed text-center">
            Mietverträge, Kontoauszüge und andere PDFs automatisch verarbeiten
          </p>
          
          {/* Visual Preview */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <FileText className="h-4 w-4" />
              <span>Vertrag.pdf</span>
              <ArrowRight className="h-4 w-4 ml-auto" />
              <Database className="h-4 w-4" />
            </div>
          </div>
          
          {/* Features */}
          <div className="space-y-2 mb-4 flex flex-col items-center">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>OCR-Erkennung</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Automatische Datenextraktion</span>
            </div>
          </div>
          
          {/* File Format Hint */}
          <div className="text-xs text-gray-500 mb-6 px-3 py-2 bg-gray-50 rounded-lg text-center">
            <span className="font-medium">Unterstützt:</span> PDF, JPG, PNG
          </div>
          
          {/* Start Button (appears on hover) */}
          <button className="w-full bg-gradient-to-r from-red-500 to-orange-500 text-white py-3 rounded-lg font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
            Jetzt starten
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>

        {/* Excel Card */}
        <div 
          style={{maxHeight: '600px', overflowY: 'hidden'}} className="group relative bg-white/95 backdrop-blur-md p-8 rounded-2xl shadow-2xl cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.4)] flex flex-col"
          onClick={() => handlePathSelection('EXCEL')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-emerald-400 opacity-0 group-hover:opacity-20 rounded-2xl transition-opacity duration-300"></div>
          
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-green-400 blur-2xl opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
            <Database className="relative h-16 w-16 text-green-500 mx-auto group-hover:scale-110 group-hover:animate-pulse transition-transform duration-300" />
          </div>
          
          <h3 className="text-2xl font-bold mb-3 text-gray-900 text-center">Excel/CSV-Listen</h3>
          <p className="text-base text-gray-600 mb-6 leading-relaxed text-center">
            Bestehende Mieter- und Objektlisten importieren
          </p>
          
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Database className="h-4 w-4" />
              <span>Mieterliste.xlsx</span>
              <ArrowRight className="h-4 w-4 ml-auto" />
              <Users className="h-4 w-4" />
            </div>
          </div>
          
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Spalten-Mapping</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Bulk-Import</span>
            </div>
          </div>
          
          {/* File Format Hint */}
          <div className="text-xs text-gray-500 mb-6 px-3 py-2 bg-gray-50 rounded-lg text-center">
            <span className="font-medium">Unterstützt:</span> CSV, XLSX, OpenDocument
          </div>
          
          <button className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-lg font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
            Jetzt starten
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>

        {/* Software Card */}
        <div 
          style={{maxHeight: '600px', overflowY: 'hidden'}} className="group relative bg-white/95 backdrop-blur-md p-8 rounded-2xl shadow-2xl cursor-pointer transition-all duration-300 hover:scale-105 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.4)] flex flex-col"
          onClick={() => handlePathSelection('SOFTWARE')}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-purple-400 to-indigo-400 opacity-0 group-hover:opacity-20 rounded-2xl transition-opacity duration-300"></div>
          
          <div className="relative mb-6">
            <div className="absolute inset-0 bg-purple-400 blur-2xl opacity-0 group-hover:opacity-50 transition-opacity duration-300"></div>
            <RefreshCw className="relative h-16 w-16 text-purple-500 mx-auto group-hover:scale-110 group-hover:rotate-180 group-hover:animate-pulse transition-all duration-300" />
          </div>
          
          <h3 className="text-2xl font-bold mb-3 text-gray-900 text-center">Software-Export</h3>
          <p className="text-base text-gray-600 mb-6 leading-relaxed text-center">
            DATEV oder andere Hausverwaltungssoftware migrieren
          </p>
          
          <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <RefreshCw className="h-4 w-4" />
              <span>DATEV-Export.zip</span>
              <ArrowRight className="h-4 w-4 ml-auto" />
              <Home className="h-4 w-4" />
            </div>
          </div>
          
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>DATEV-Integration</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>Automatische Migration</span>
            </div>
          </div>
          
          {/* File Format Hint */}
          <div className="text-xs text-gray-500 mb-6 px-3 py-2 bg-gray-50 rounded-lg text-center">
            <span className="font-medium">Unterstützt:</span> DATEV, HausPerfekt, Domus
          </div>
          
          <button className="w-full bg-gradient-to-r from-purple-500 to-indigo-500 text-white py-3 rounded-lg font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2">
            Jetzt starten
            <ArrowRight className="h-5 w-5" />
          </button>
        </div>
      </div>
      </div>
    </div>
  );

  const renderUpload = () => (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          {selectedPath === 'PDF' && 'PDF-Dokumente hochladen'}
          {selectedPath === 'EXCEL' && 'Excel/CSV-Dateien hochladen'}
          {selectedPath === 'SOFTWARE' && 'Software-Export hochladen'}
        </h2>
        <p className="text-gray-600">
          {selectedPath === 'PDF' && 'Laden Sie Mietverträge, Kontoauszüge oder andere relevante PDFs hoch'}
          {selectedPath === 'EXCEL' && 'Laden Sie Ihre Excel- oder CSV-Dateien mit Mieter- und Objektdaten hoch'}
          {selectedPath === 'SOFTWARE' && 'Laden Sie den Export Ihrer bisherigen Hausverwaltungssoftware hoch'}
        </p>
      </div>

      <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
        <input
          type="file"
          multiple
          accept={
            selectedPath === 'PDF' ? '.pdf' :
            selectedPath === 'EXCEL' ? '.xlsx,.csv' :
            '.zip,.csv,.xlsx'
          }
          onChange={(e) => handleFileUpload(Array.from(e.target.files))}
          className="hidden"
          id="file-upload"
        />
        <label htmlFor="file-upload" className="cursor-pointer">
          <Upload className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Dateien hier ablegen oder klicken zum Auswählen
          </h3>
          <p className="text-gray-500">
            {selectedPath === 'PDF' && 'Unterstützte Formate: PDF'}
            {selectedPath === 'EXCEL' && 'Unterstützte Formate: XLSX, CSV'}
            {selectedPath === 'SOFTWARE' && 'Unterstützte Formate: ZIP, CSV, XLSX'}
          </p>
        </label>
      </div>

      {isProcessing && (
        <div className="mt-8">
          <div className="bg-white rounded-lg p-6 border">
            <div className="flex items-center mb-4">
              <RefreshCw className="h-5 w-5 text-blue-500 animate-spin mr-3" />
              <span className="font-medium">Dateien werden verarbeitet...</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${processingProgress}%` }}
              ></div>
            </div>
            <div className="text-sm text-gray-600 mt-2">
              {processingProgress}% abgeschlossen
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-3" />
          <span className="text-red-700">{error}</span>
        </div>
      )}
    </div>
  );

  const renderPreview = () => (
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">
          Extrahierte Daten prüfen
        </h2>
        <p className="text-gray-600">
          Überprüfen Sie die automatisch erkannten Daten vor dem Import
        </p>
      </div>

      {extractedData.map((item, index) => (
        <div key={index} className="bg-white rounded-lg border p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            {item.file}
          </h3>

          {item.type === 'contract' && (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Vermieter</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>Name:</strong> {item.data.landlord}</div>
                  <div><strong>Email:</strong> {item.data.landlordEmail}</div>
                  <div><strong>Telefon:</strong> {item.data.landlordPhone}</div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Mieter</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>Name:</strong> {item.data.tenantName}</div>
                  <div><strong>Email:</strong> {item.data.tenantEmail}</div>
                  <div><strong>Miete:</strong> {item.data.monthlyRent}€</div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Objekt</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>Adresse:</strong> {item.data.propertyAddress}</div>
                  <div><strong>Wohnung:</strong> {item.data.unitIdentifier}</div>
                  <div><strong>Größe:</strong> {item.data.sizeSqm} qm</div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Mietvertrag</h4>
                <div className="space-y-2 text-sm">
                  <div><strong>Beginn:</strong> {item.data.leaseStart}</div>
                  <div><strong>Ende:</strong> {item.data.leaseEnd || 'Unbefristet'}</div>
                  <div><strong>Kaution:</strong> {item.data.deposit}€</div>
                </div>
              </div>
            </div>
          )}

          {item.type === 'excel' && (
            <div>
              <p className="text-sm text-gray-600 mb-4">
                {item.data.length} Datensätze erkannt
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2">Objekt</th>
                      <th className="text-left p-2">Wohnung</th>
                      <th className="text-left p-2">Mieter</th>
                      <th className="text-left p-2">Miete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {item.data.slice(0, 5).map((row, i) => (
                      <tr key={i} className="border-b">
                        <td className="p-2">{row.propertyName || row.address}</td>
                        <td className="p-2">{row.unitIdentifier}</td>
                        <td className="p-2">{row.tenantName}</td>
                        <td className="p-2">{row.monthlyRent}€</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {item.data.length > 5 && (
                  <p className="text-xs text-gray-500 mt-2">
                    ... und {item.data.length - 5} weitere Datensätze
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-600">
              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
              Konfidenz: {Math.round(item.data.confidence * 100)}%
            </div>
          </div>
        </div>
      ))}

      <div className="flex justify-between">
        <button 
          onClick={() => setStage('upload')}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Zurück
        </button>
        <button 
          onClick={handleDataConfirmation}
          className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
          disabled={isProcessing}
        >
          {isProcessing ? (
            <>
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
              Importiere...
            </>
          ) : (
            <>
              Daten importieren
              <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );

  const renderSuccess = () => (
    <div className="max-w-4xl mx-auto text-center">
      <div className="bg-white rounded-lg p-8 border">
        <CheckCircle className="h-24 w-24 text-green-500 mx-auto mb-6" />
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          Import erfolgreich abgeschlossen!
        </h1>
        <p className="text-lg text-gray-600 mb-8">
          Ihre Daten wurden erfolgreich in Clara360 importiert
        </p>

        {results && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <div className="p-4 bg-blue-50 rounded-lg">
              <Building className="h-8 w-8 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-900">{results.owners.length}</div>
              <div className="text-sm text-blue-700">Eigentümer</div>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <Home className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-900">{results.properties.length}</div>
              <div className="text-sm text-green-700">Objekte</div>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <CreditCard className="h-8 w-8 text-purple-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-purple-900">{results.units.length}</div>
              <div className="text-sm text-purple-700">Wohnungen</div>
            </div>
            <div className="p-4 bg-orange-50 rounded-lg">
              <Users className="h-8 w-8 text-orange-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-orange-900">{results.tenants.length}</div>
              <div className="text-sm text-orange-700">Mieter</div>
            </div>
          </div>
        )}

        {results?.errors?.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
            <h3 className="font-medium text-yellow-800 mb-2">Hinweise:</h3>
            <ul className="text-sm text-yellow-700 space-y-1">
              {results.errors.map((error, index) => (
                <li key={index}>• {error}</li>
              ))}
            </ul>
          </div>
        )}

        <button 
          onClick={() => {
            // Celebration confetti
            confetti({
              particleCount: 120,
              spread: 70,
              origin: { y: 0.6 },
              colors: ['#06b6d4', '#8b5cf6', '#ec4899', '#3b82f6']
            });
            // Navigate to dashboard after short delay
            setTimeout(() => {
            if (typeof onComplete === 'function') onComplete();
            navigate('/', { state: { onboardingCompleted: true } });
          }, 500);
          }}
          className="px-8 py-3 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 text-white rounded-lg hover:scale-105 transition-transform duration-300 text-lg font-semibold shadow-xl"
        >
          🎉 Zum Dashboard
        </button>
      </div>
    </div>
  );

  const renderMapping = () => {
    if (!extractedData || extractedData.length === 0) return null;

    const firstExtract = extractedData[0];
    
    const handleMappingComplete = (mappingResult) => {
      setMappedData(mappingResult);
      setShowFieldMapper(false);
      setStage('preview');
    };

    const handleMappingCancel = () => {
      setShowFieldMapper(false);
      setStage('upload');
    };

    // Render appropriate mapper based on data type
    if (firstExtract.type === 'excel') {
      return (
        <ExcelFieldMapper
          excelData={firstExtract.data}
          onMappingComplete={handleMappingComplete}
          onCancel={handleMappingCancel}
        />
      );
    } else if (firstExtract.type === 'datev') {
      return (
        <DATEVFieldMapper
          datevData={firstExtract.data}
          onMappingComplete={handleMappingComplete}
          onCancel={handleMappingCancel}
        />
      );
    } else if (firstExtract.type === 'contract') {
      return (
        <PDFContractMapper
          extractedData={firstExtract.data}
          onMappingComplete={handleMappingComplete}
          onCancel={handleMappingCancel}
        />
      );
    }

    return null;
  };

  // ✅ FIX: Show loading state while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Initialisiere...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="container mx-auto">
        {/* Progress indicator */}
        {stage !== 'welcome' && (
          <div className="max-w-4xl mx-auto mb-8">
            <div className="flex items-center justify-center space-x-4 text-gray-600">
              <div className={`flex items-center ${stage === 'upload' ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">1</div>
                Upload
              </div>
              <div className="w-8 h-0.5 bg-gray-300"></div>
              <div className={`flex items-center ${stage === 'mapping' ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">2</div>
                Mapping
              </div>
              <div className="w-8 h-0.5 bg-gray-300"></div>
              <div className={`flex items-center ${stage === 'preview' ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">3</div>
                Prüfung
              </div>
              <div className="w-8 h-0.5 bg-gray-300"></div>
              <div className={`flex items-center ${stage === 'success' ? 'text-blue-600' : 'text-gray-400'}`}>
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center mr-2">4</div>
                Import
              </div>
            </div>
          </div>
        )}

        {/* Stage content */}
        {stage === 'welcome' && renderWelcome()}
        {stage === 'upload' && renderUpload()}
        {stage === 'mapping' && renderMapping()}
        {stage === 'preview' && renderPreview()}
        {stage === 'success' && renderSuccess()}
      </div>
    </div>
  );
};

export default RealOnboardingSystem;
